// Summary: Shared tolerant text matching for POS search boxes — normalizes Hebrew punctuation,
// expands query tokens through an optional synonym map, and allows a small typo budget so
// "מגירא", "setings" or "cola" still reach their target.
// Details: docs/function-library/web/utils.md#semantic-search

// Quotes/geresh/gershayim/niqqud/hyphens are typed inconsistently (מע"מ vs מעמ, צ'יפס vs צפס),
// so they are stripped on both the query and the haystack.
export const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\u0591-\u05C7]/g, '')
    .replace(/["'`\u05F3\u05F4\u2018\u2019\u201C\u201D\-_/]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Final letters are the same consonant, so ק-ר-א-ף and ק-ר-א-פ must fold together.
const FINAL_LETTERS: Record<string, string> = { 'ם': 'מ', 'ן': 'נ', 'ך': 'כ', 'ף': 'פ', 'ץ': 'צ' };

const foldFinalLetters = (value: string): string =>
  value.replace(/[םןךףץ]/g, (letter) => FINAL_LETTERS[letter] ?? letter);

// Hebrew spelling varies by matres lectionis (כתיב מלא/חסר): אוכמניות vs אכמניות, קראף vs קרף.
// Dropping א/ו/י leaves a consonant skeleton that both spellings share.
const hebrewSkeleton = (value: string): string => foldFinalLetters(value).replace(/[אוי]/g, '');

// Bounded Damerau-Levenshtein (OSA): counts an adjacent swap as ONE edit, so קארף→קראף costs 1.
// Bails out as soon as the distance is known to exceed `max`.
const withinEditDistance = (a: string, b: string, max: number): boolean => {
  if (Math.abs(a.length - b.length) > max) {
    return false;
  }
  let beforePrevious: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current: number[] = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(
        (current[j - 1] ?? 0) + 1,
        (previous[j] ?? 0) + 1,
        (previous[j - 1] ?? 0) + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, (beforePrevious[j - 2] ?? 0) + 1);
      }
      current.push(value);
      rowBest = Math.min(rowBest, value);
    }
    if (rowBest > max) {
      return false;
    }
    beforePrevious = previous;
    previous = current;
  }
  return (previous[b.length] ?? max + 1) <= max;
};

// A typo budget only for words long enough that a single edit stays unambiguous.
const fuzzyHit = (token: string, word: string): boolean => {
  if (token.length < 4) {
    return false;
  }
  return withinEditDistance(token, word, token.length >= 7 ? 2 : 1);
};

const variantHits = (variant: string, haystack: string[]): boolean =>
  haystack.some((entry) => {
    if (entry.includes(variant)) {
      return true;
    }
    const words = entry.split(' ');
    if (words.some((word) => fuzzyHit(variant, word))) {
      return true;
    }
    // Spelling-variant pass: compare consonant skeletons, and allow a typo on top of them.
    // Gated at 2 consonants so a partial query stays as usable here as a plain substring is.
    const variantSkeleton = hebrewSkeleton(variant);
    if (variantSkeleton.length < 2) {
      return false;
    }
    if (hebrewSkeleton(entry).includes(variantSkeleton)) {
      return true;
    }
    return words.some((word) => fuzzyHit(variantSkeleton, hebrewSkeleton(word)));
  });

/**
 * Every query token must land somewhere, so "דוח עובד" narrows instead of widening.
 * `synonyms` keys must already be normalized; each token is tried alongside its aliases.
 */
export const matchesSearchQuery = (
  rawQuery: string,
  haystack: string[],
  synonyms?: Record<string, string[]>,
): boolean => {
  const tokens = normalizeSearchText(rawQuery).split(' ').filter(Boolean);
  if (tokens.length === 0) {
    return true;
  }
  return tokens.every((token) => {
    if (variantHits(token, haystack)) {
      return true;
    }
    const aliases = synonyms?.[token];
    return aliases != null && aliases.some((alias) => variantHits(normalizeSearchText(alias), haystack));
  });
};
